import { observer } from "mobx-react-lite";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDelayedFlag, useTran } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PATHS } from "@/lib/router";
import { useKpiTargetsStore, useFrameworkData } from "../hooks";
import TargetYearCard from "./TargetYearCard";
import TargetSettingForm from "./TargetSettingForm";
import FrameworkTargetsSkeleton from "./skeleton/FrameworkTargetsSkeleton";

const FrameworkTargetsSection = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const {
    activeCategory,
    isEnvOnly,
    loadingDisclosures,
    loadingTargets,
    selectedFramework,
    setActiveCategory,
    setSelectedFramework,
    setTargetSearchTerm,
    targetSearchTerm,
  } = useKpiTargetsStore();

  const {
    visibleCategories,
    visibleFrameworks,
    frameworkCounts,
    loadingFrameworks,
  } = useFrameworkData();
  const isLoading = loadingFrameworks || loadingDisclosures || loadingTargets;
  const showSkeleton = useDelayedFlag(isLoading);

  if (showSkeleton) {
    return <FrameworkTargetsSkeleton />;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-radius border border-border bg-card p-4">
          <div className="mb-3">
            <label className="text-sm font-medium text-foreground">
              {t("Framework")}
            </label>
            <p className="text-xs text-muted-foreground">
              {t("Select framework to set targets for its disclosures.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={selectedFramework === "All" ? "default" : "outline"}
              onClick={() => setSelectedFramework("All")}
            >
              {t("All")}
              <span className="ml-2 rounded bg-background/20 px-1.5 py-0.5 text-xs">
                {frameworkCounts.All || 0}
              </span>
            </Button>

            {loadingFrameworks ? (
              <span className="text-sm text-muted-foreground">
                {t("Loading frameworks...")}
              </span>
            ) : (
              visibleFrameworks.map((framework) => (
                <Button
                  key={framework.code}
                  size="sm"
                  variant={
                    selectedFramework === framework.code ? "default" : "outline"
                  }
                  onClick={() => setSelectedFramework(framework.code)}
                >
                  {framework.code}
                  <span className="ml-2 rounded bg-background/20 px-1.5 py-0.5 text-xs">
                    {frameworkCounts[framework.code] || 0}
                  </span>
                </Button>
              ))
            )}
          </div>
        </div>

        <TargetYearCard />
      </div>

      <div className="flex flex-col gap-2 rounded-radius border border-border bg-card p-4">
        <label className="text-sm font-medium text-foreground">
          {t("Search disclosures")}
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={targetSearchTerm}
              onChange={(event) => setTargetSearchTerm(event.target.value)}
              placeholder={t("Search by code, title, framework, or category")}
              className="pl-9 pr-10"
            />
            {targetSearchTerm ? (
              <button
                type="button"
                onClick={() => setTargetSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted"
                aria-label={t("Clear search")}
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          {targetSearchTerm.trim() ? (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {t("Filtering")}
            </span>
          ) : null}
        </div>
      </div>

      {visibleCategories.length > 0 ? (
        <div className="flex items-center justify-between gap-4 border-b border-border pb-2">
          <div className="flex flex-wrap gap-2">
            {!isEnvOnly ? (
              <Button
                size="sm"
                variant={activeCategory === "All" ? "default" : "ghost"}
                onClick={() => setActiveCategory("All")}
              >
                {t("All")}
              </Button>
            ) : null}
            {visibleCategories.map((category) => (
              <Button
                key={category}
                size="sm"
                variant={activeCategory === category ? "default" : "ghost"}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(PATHS.ESG_TARGETS_SUMMARY)}
          >
            {t("View Summary")}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => navigate(PATHS.ESG_TARGETS_SUMMARY)}
          >
            {t("View Summary")}
          </Button>
        </div>
      )}

      <TargetSettingForm />
    </>
  );
});

export default FrameworkTargetsSection;
